import React from 'react';

import { Body2, PrimaryButton } from '@rbilabs/components-library/build';
import { Field, Formik, FormikErrors, useFormikContext } from 'formik';
import { IntlFormatters, useIntl } from 'react-intl';

import { Mutable, Nullable } from '@rbi-ctg/frontend';
import { ActionButton } from 'components/action-button';
import { useDeliveryAddressModalContext } from 'components/address-modal/delivery-address-modal.context';
import { PinIcon } from 'components/address-modal/pin-icon';
import { FormikTextArea } from 'components/formik/text-area';
import { FormikTextInput } from 'components/formik/text-input';
import { IDeliveryAddress as GatewayDeliveryAddress } from 'generated/graphql-gateway';
import { IDeliveryAddress as RbiDeliveryAddress } from 'generated/rbi-graphql';
import { StoreLocatorSteps } from 'pages/store-locator/types';

import {
  DeleteButtonContainer,
  EditForm,
  FieldWrapper,
  FormWrapper,
  Header,
  HeaderContainer,
  SaveButtonContainer,
} from './styled';
import { useDeleteSavedAddress } from './use-delete-saved-address';
import { useEditDeliveryAddress } from './use-edit-delivery-address';

type CommonProperties = {
  country?: string | null;
};

type TDeliveryAddress = Omit<GatewayDeliveryAddress, '__typename' | keyof CommonProperties> &
  Omit<RbiDeliveryAddress, '__typename' | keyof CommonProperties> &
  CommonProperties;

type TDeliveryAddressPayload = Omit<TDeliveryAddress, '__typename'>;

interface EditAddressFormProps {
  address: TDeliveryAddress;
  setEditedAddress: (address: TDeliveryAddress) => void;
  setCurrentStep: (step: StoreLocatorSteps) => void;
}

const getAddressLabel = (address: TDeliveryAddress) =>
  `${address.route} ${address.streetNumber}, ${address.city}, ${address.state} ${
    address.zip
  }, ${address.country?.toLocaleUpperCase()}`;

const AddressHeader = () => {
  const { values } = useFormikContext<TDeliveryAddress>();

  return (
    <HeaderContainer>
      <PinIcon />
      <Header>
        <h2>{values.alias}</h2>
        <span>{getAddressLabel(values)}</span>
      </Header>
    </HeaderContainer>
  );
};

// maybe we can use validation schemas with Yup or Zod?
const validateDeliveryAddress =
  (formatMessage: IntlFormatters['formatMessage']) => async (values: TDeliveryAddressPayload) => {
    const errors: FormikErrors<Mutable<TDeliveryAddressPayload>> = {};

    if (!values.route) {
      errors.route = formatMessage({ id: 'streetNameRequired' });
    }

    if (!values.streetNumber) {
      errors.streetNumber = formatMessage({ id: 'streetNumberRequired' });
    }

    if (!values.alias) {
      errors.alias = formatMessage({ id: 'addressNameRequired' });
    }

    return errors;
  };

export const EditAddressForm: React.FC<EditAddressFormProps> = ({
  address,
  setEditedAddress,
  setCurrentStep,
}) => {
  const { formatMessage } = useIntl();
  const { setDeliveryAddress } = useDeliveryAddressModalContext();

  const { RemoveSavedAddressModal, openRemoveSavedAddressModal, removeSavedAddressModalBody } =
    useDeleteSavedAddress({
      setCurrentStep,
      setDeliveryAddress,
    });

  const { loading, updateDeliveryAddress } = useEditDeliveryAddress({
    setEditedAddress,
    setCurrentStep,
  });

  return (
    <>
      <Formik
        onSubmit={updateDeliveryAddress}
        validate={validateDeliveryAddress(formatMessage)}
        initialValues={{
          route: address?.route ?? '',
          addressLine1: address?.addressLine1 ?? '',
          addressLine2: address?.addressLine2 ?? '',
          streetNumber: address?.streetNumber ?? '',
          phoneNumber: address?.phoneNumber ?? '',
          alias: address?.alias ?? '',
          city: address?.city ?? '',
          state: address?.state ?? '',
          country: address?.country ?? '',
          zip: address?.zip ?? '',
          deliveryInstructions: '',
          latitude: address?.latitude ?? null,
          longitude: address?.longitude ?? null,
        }}
      >
        {({ handleSubmit, isValid }) => {
          return (
            <EditForm onSubmit={handleSubmit} noValidate>
              <FormWrapper>
                <AddressHeader />

                <Field
                  required
                  name="route"
                  component={FormikTextInput}
                  data-testid="street-name-input"
                  label={formatMessage({ id: 'streetName' })}
                />

                <FieldWrapper>
                  <Field
                    required
                    name="streetNumber"
                    component={FormikTextInput}
                    data-testid="street-number-input"
                    label={formatMessage({ id: 'streetNumber' })}
                  />
                  <Field
                    name="addressLine2"
                    component={FormikTextInput}
                    data-testid="address-line-two-input"
                    label={formatMessage({ id: 'apartmentLabel' })}
                  />
                </FieldWrapper>

                <FieldWrapper>
                  <Field
                    name="phoneNumber"
                    component={FormikTextInput}
                    data-testid="phone-number-input"
                    label={formatMessage({ id: 'phoneNumberOptional' })}
                  />
                  <Field
                    disabled
                    name="alias"
                    component={FormikTextInput}
                    data-testid="address-name-input"
                    label={formatMessage({ id: 'addressName' })}
                  />
                </FieldWrapper>

                <Field
                  component={FormikTextArea}
                  name="deliveryInstructions"
                  data-testid="delivery-instructions-input"
                  label={formatMessage({ id: 'deliveryDetailsInstructions' })}
                />

                <Body2>{formatMessage({ id: 'deliveryPhoneNumberInfo' })}</Body2>
              </FormWrapper>

              <DeleteButtonContainer>
                <PrimaryButton
                  fullWidth
                  size="small"
                  type="button"
                  icon="deleteAccount"
                  reversed
                  onClick={() =>
                    openRemoveSavedAddressModal(address as Nullable<RbiDeliveryAddress>)
                  }
                >
                  {formatMessage({ id: 'deleteAddress' })}
                </PrimaryButton>
              </DeleteButtonContainer>

              <SaveButtonContainer>
                <ActionButton
                  fullWidth
                  isLoading={loading}
                  disabled={!isValid}
                  data-testid="save-address"
                >
                  {formatMessage({ id: 'saveAddress' })}
                </ActionButton>
              </SaveButtonContainer>
            </EditForm>
          );
        }}
      </Formik>

      <RemoveSavedAddressModal
        showCloseButton
        body={removeSavedAddressModalBody}
        data-testid="remove-saved-address-modal"
        buttonLabel={formatMessage({ id: 'deleteButtonText' })}
        cancelLabel={formatMessage({ id: 'keepAddressButton' })}
        heading={formatMessage({ id: 'removeSavedAddressTitleAlt' })}
      />
    </>
  );
};
